import {
  calculateAverageOfSectionScores,
  calculateAverageOfSectionScoresAfterSession
} from "seneca-common/features/session/logic/courseScore";
import {
  calculateAverageContentProficiency,
  calculateAverageContentProficiencyWithFade
} from "seneca-common/features/session/logic/proficiency";
import { allStatsHaveFetchedForCourseIfNeeded } from "seneca-common/features/stats/selectors/meta";
import {
  getContentStats,
  getCourseProficiency as getRoughCourseProficiencySnapshot,
  getCourseScore,
  getStatsForSections
} from "seneca-common/features/stats/state";

import { getAllCourseContentIds, makeGetCourseLeafSectionIds } from "../state";
import { getAllCourseExerciseContentIds } from "../state/selectors/getAllCourseContentIds";

type State = any;

export { getCourseScore };

export function calculateCourseScore(state: State, courseId: string): number {
  const studyableSectionIds = makeGetCourseLeafSectionIds(courseId)(state);
  const sectionStats = getStatsForSections(state, studyableSectionIds);
  return calculateAverageOfSectionScores({
    sectionStats,
    totalSections: studyableSectionIds.size
  });
}

export function calculateCourseScoreAfterSession(
  state: State,
  courseId: string,
  sessionSectionId: string,
  sessionScore: number
): number {
  const studyableSectionIds = makeGetCourseLeafSectionIds(courseId)(state);
  const sectionStats = getStatsForSections(state, studyableSectionIds);
  return calculateAverageOfSectionScoresAfterSession({
    sectionStats,
    totalSections: studyableSectionIds.size,
    sessionSectionId,
    sessionScore
  });
}

export function getCourseProficiency(state: State, courseId: string): number {
  if (allStatsHaveFetchedForCourseIfNeeded(state, courseId)) {
    return getDetailedCourseProficiency(state, courseId);
  } else {
    return getRoughCourseProficiency(state, courseId);
  }
}

export function getCourseProficiencyWithFade(
  state: State,
  courseId: string,
  timestamp: string
): number {
  if (allStatsHaveFetchedForCourseIfNeeded(state, courseId)) {
    return getDetailedCourseProficiencyWithFade(state, courseId, timestamp);
  } else {
    // Can't calculate fade because we don't have spacing for course.
    // Need to fetch all sections when capability is available
    return getRoughCourseProficiency(state, courseId);
  }
}

export function getDetailedCourseProficiency(
  state: State,
  courseId: string
): number {
  const normalContentIds = getAllCourseContentIds(state, courseId);
  const exerciseContentIds = getAllCourseExerciseContentIds(state, courseId);
  const contentIds = normalContentIds.merge(exerciseContentIds);

  const contentStats = getContentStats(state, contentIds).toList();

  return calculateAverageContentProficiency(contentStats, contentIds.size);
}

export function getRoughCourseProficiency(
  state: State,
  courseId: string
): number {
  return getRoughCourseProficiencySnapshot(state, courseId);
}

export function getDetailedCourseProficiencyWithFade(
  state: State,
  courseId: string,
  timestamp: string
): number {
  const normalContentIds = getAllCourseContentIds(state, courseId);
  const exerciseContentIds = getAllCourseExerciseContentIds(state, courseId);
  const contentIds = normalContentIds.merge(exerciseContentIds);

  const contentStats = getContentStats(state, contentIds).toList();

  return calculateAverageContentProficiencyWithFade(
    contentStats,
    contentIds.size,
    timestamp
  );
}
